import { cn, useTranslation } from '@hbs-us/ui';
import { Link } from '@tanstack/react-router';

import type { EstimateStatus } from '../contractsMirror.js';
import type { EstimatesSearch } from '../searchParams.js';

/**
 * The status-filter pill row on the estimates list (legacy `EstimatesListPage`
 * "Filter by Status"). Clones the reports `RangeFilter` pill→navigate pattern:
 * each pill is a `<Link>` that flips `?status` in the typed search params, which
 * drives exactly one coarse-endpoint refetch. Each pill shows its SERVER-computed
 * count (21-05 `statusCounts`, filter-independent so the counts stay stable while
 * a filter is active). The active pill is the one place the orange accent is used
 * here (`bg-primary text-primary-foreground`); 44px targets; pills wrap (ES
 * layout obligation, I18N-05). Counts in IBM Plex Mono.
 */

const PILL_STATUSES: readonly EstimateStatus[] = ['draft', 'ready', 'sent', 'approved', 'rejected'];

function pillSearch(status: EstimateStatus | null, showArchived: boolean): EstimatesSearch {
  const search: EstimatesSearch = {};
  if (status) search.status = status;
  if (showArchived) search.show_archived = true;
  return search;
}

const PILL_BASE =
  'inline-flex h-11 items-center gap-2 rounded-md border px-4 font-medium text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background';
const PILL_ACTIVE = 'border-transparent bg-primary text-primary-foreground';
const PILL_INACTIVE = 'border-border bg-card text-foreground hover:bg-secondary';

export function StatusFilterPills({
  search,
  statusCounts,
  totalCount,
}: {
  search: EstimatesSearch;
  statusCounts: Partial<Record<EstimateStatus, number>>;
  totalCount: number;
}) {
  const { t } = useTranslation();
  const showArchived = Boolean(search.show_archived);
  const active = search.status ?? null;

  return (
    <section
      aria-label={t('estimates:filter.regionLabel')}
      className="mb-4 rounded-md border border-border bg-card p-6"
    >
      <h2 className="mb-3 font-semibold text-foreground text-sm">
        {t('estimates:filter.heading')}
      </h2>
      <div className="flex flex-wrap gap-2">
        <Link
          to="/estimates"
          search={pillSearch(null, showArchived)}
          aria-current={active === null ? 'true' : undefined}
          className={cn(PILL_BASE, active === null ? PILL_ACTIVE : PILL_INACTIVE)}
        >
          {t('estimates:filter.all')}
          <span className="font-mono text-xs opacity-80">{totalCount}</span>
        </Link>
        {PILL_STATUSES.map((status) => {
          const isActive = active === status;
          return (
            <Link
              key={status}
              to="/estimates"
              search={pillSearch(status, showArchived)}
              aria-current={isActive ? 'true' : undefined}
              className={cn(PILL_BASE, isActive ? PILL_ACTIVE : PILL_INACTIVE)}
            >
              {t(`estimates:filter.${status}`)}
              <span className="font-mono text-xs opacity-80">{statusCounts[status] ?? 0}</span>
            </Link>
          );
        })}
      </div>
    </section>
  );
}
